import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Tooltip, useMediaQuery } from '@mui/material';
// the classic vocabulary (what every page not yet migrated wears)
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import AllocationInfoHeader from 'src/components/AllocationInfoHeader';
import {
  Eyebrow as ClassicEyebrow,
  Fact as ClassicFact,
  FactGroup as ClassicFactGroup,
  Panel as ClassicPanel,
  SectionCard as ClassicSectionCard
} from 'src/components/record';
import {
  TEAM_FONT,
  accentInkFor,
  teamPillSx,
  teamPrimaryButtonSx,
  teamSecondaryButtonSx,
  teamSoftButtonSx,
  teamToneButtonSx
} from 'src/sections/@dashboard/user/teamTokens';
import { INDIGO } from 'src/layouts/auth/portals';
import { TABLE_FONT, tableFooterSx, tableInkFor, tablePaginationSx } from 'src/components/table/tableTokens';
import { fCurrency, fNumber } from 'src/utils/formatNumber';
// the Universe vocabulary
import Icon from './Icon';
import Status from './Status';
import {
  Eyebrow as UniverseEyebrow,
  Fact as UniverseFact,
  FactGroup as UniverseFactGroup,
  Section,
  embeddedHeadCellSx,
  embeddedHeadSx,
  embeddedTableSx,
  formControlsSx,
  pickerTableSx,
  quietHeadCellSx,
  quietHeadSx,
  rowRuleOf,
  secondaryTableSx,
  segmentSx,
  segmentedTrackSx,
  sheetSx,
  wellSx
} from './Surface';
import DialogHead, { DialogFooter, dialogPaperSx } from './layers/DialogHead';
import {
  destructiveButtonSx,
  ghostButtonSx,
  iconButtonSx,
  primaryButtonSx,
  secondaryButtonSx,
  softButtonSx,
  toneButtonSx
} from 'src/theme/universe/buttonSx';
import {
  RADIUS,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  fillOf,
  hairlineOf,
  sunkenOf
} from 'src/theme/universe/brand';
import {
  footerSx,
  menuPaperSx,
  paginationSx,
  primaryHeadSx,
  textTabsSx
} from 'src/theme/universe/surfaceSx';
import { toneInkOf, toneTintOf } from 'src/theme/universe/tones';
import { fCount, fHours, fMoney, fPercent } from 'src/utils/universeFormat';

// ---------------------------------------------------------------------------
// THE SKIN OF A PAGE.
//
// The translation project workspace was migrated to DESIGN.md on 07/10/2026,
// but the pieces it is made of — the step bar, the step panel, the
// financials board, the rails, the allocation rows, the candidate finder,
// the request pop-ups, the dialogs and the classic forms behind them — are
// shared with the interpretation and mediation workspace, the classic
// allocation pages, the vendor pages and the patent pages, which have not
// migrated. Restyling them in place would have changed forty pages at once.
//
// So a page CHOOSES its skin: the translation workspace mounts
// `<UniverseSkin>`, and every shared piece reads its recipes through
// `useSkin()` — the button shapes, the status chip, the section head, the
// dialog head, the column heads, the glyph family, the figure formats. With
// no provider the hook resolves to the CLASSIC recipes, which are the very
// components and tokens those pieces imported before (Label,
// AllocationInfoHeader, components/record, teamTokens), so a page that has
// not chosen a skin renders exactly as it did.
//
// Both skins answer the same keys. A caller never tests the skin's name to
// pick a colour or a size; it asks for `skin.button.primary` or
// `skin.money(value)` and gets the right one.
// ---------------------------------------------------------------------------

export const SkinContext = createContext('classic');

UniverseSkin.propTypes = { children: PropTypes.node };

export function UniverseSkin({ children }) {
  return <SkinContext.Provider value="universe">{children}</SkinContext.Provider>;
}

/** The skin's name only — for the rare branch a recipe cannot express. */
export const useSkinName = () => useContext(SkinContext);

// ---------------------------------------------------------------------------
// GLYPHS. `classic` is the Iconify name a piece drew before; `name` is the
// Iconsax registry name it draws on the Universe skin. A piece passes both,
// and the branch it is not on ignores the other.

ClassicGlyph.propTypes = {
  classic: PropTypes.string,
  size: PropTypes.number,
  sx: PropTypes.object
};

function ClassicGlyph({ classic, size = 16, sx, name, variant, solid, label, ...other }) {
  if (!classic) return null;
  // `name`, `variant`, `solid` and `label` are the Universe glyph's; on the
  // classic branch they would reach the DOM, where React warns on a boolean.
  return <Iconify icon={classic} width={size} height={size} sx={sx} aria-label={label} {...other} />;
}

UniverseGlyph.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
  variant: PropTypes.string,
  /** The outline FILLED, for the glyphs whose Bold form is wrong (the star). */
  solid: PropTypes.bool,
  sx: PropTypes.object,
  label: PropTypes.string
};

function UniverseGlyph({ name, size = 16, variant, solid, sx, label }) {
  if (!name) return null;
  return <Icon name={name} size={size} variant={variant} solid={solid} sx={sx} label={label} />;
}

// ---------------------------------------------------------------------------
// THE STATUS CHIP. The classic page draws `Label` (ghost by default, the
// colours of the theme ramps); the Universe skin draws the status label of
// DESIGN.md §19 — the stage's icon and the stored word on the tone's wash.

const LABEL_COLORS = [
  'default',
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
  'purple',
  'atlas',
  'fusion',
  'universe',
  'layout',
  'technologies',
  'cronostoria',
  'congressi'
];

ClassicChip.propTypes = {
  value: PropTypes.node,
  tone: PropTypes.string,
  variant: PropTypes.string,
  filled: PropTypes.bool,
  hint: PropTypes.node,
  sx: PropTypes.object
};

// A Universe tone the classic Label has no colour for reads as its nearest
// ramp: the review orange (15/10/2026) as the classic amber.
const CLASSIC_TONE_OF = { review: 'warning', soon: 'warning', accent: 'primary', neutral: 'default' };

function ClassicChip({ value, tone = 'default', variant = 'ghost', filled = false, hint, sx }) {
  const wanted = CLASSIC_TONE_OF[tone] || tone;
  const color = LABEL_COLORS.includes(wanted) ? wanted : 'default';
  const body = (
    <Label variant={filled ? 'filled' : variant} color={color} sx={sx}>
      {value}
    </Label>
  );
  if (!hint) return body;
  return (
    <Tooltip arrow title={hint}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {body}
      </Box>
    </Tooltip>
  );
}

UniverseChip.propTypes = {
  value: PropTypes.node,
  tone: PropTypes.string,
  icon: PropTypes.string,
  filled: PropTypes.bool,
  hint: PropTypes.node,
  wrap: PropTypes.bool,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object
};

// A classic colour that is not a tone of the registry (atlas, layout) reads
// as the info tone: it marks provenance, not danger.
const TONE_OF = { atlas: 'info', layout: 'info', fusion: 'info', universe: 'accent' };

function UniverseChip({ value, tone = 'default', icon, filled = false, hint, wrap = false, fullWidth = false, sx }) {
  return (
    <Status
      variant="label"
      value={typeof value === 'string' || typeof value === 'number' ? String(value) : value}
      tone={TONE_OF[tone] || tone}
      icon={icon}
      filled={filled}
      hint={hint}
      wrap={wrap}
      fullWidth={fullWidth}
      sx={sx}
    />
  );
}

// ---------------------------------------------------------------------------
// THE SECTION. The classic page's `Panel` is a hairline card; the Universe
// section is a region sheet on the canvas (DESIGN.md §7, revised 07/10/2026:
// paper, the hairline, the small shadow, a 52px head), and flat when it is
// rendered inside another section. `SectionCard` is the same thing with the
// communication log's props.

UniversePanel.propTypes = {
  title: PropTypes.node,
  note: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  id: PropTypes.string,
  sx: PropTypes.object
};

function UniversePanel({ title, note, action, children, id, sx }) {
  return (
    <Section id={id} title={title} meta={note} action={action} sx={sx}>
      {children}
    </Section>
  );
}

UniverseSectionCard.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
  contentSx: PropTypes.object
};

function UniverseSectionCard({ title, subtitle, action, children, sx, contentSx }) {
  return (
    <Section title={title} meta={subtitle} action={action} sx={sx} bodySx={contentSx}>
      {children}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// THE DIALOG HEAD. The classic strip (a tinted band with a circular icon)
// against the Universe anatomy (title 18/700, one meta line, a hairline).

UniverseDialogHead.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  projectNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linguistName: PropTypes.string,
  onClose: PropTypes.func,
  sx: PropTypes.object
};

function UniverseDialogHead({ title, subtitle, projectNo, linguistName, onClose, sx }) {
  const meta = subtitle || [projectNo, linguistName].filter(Boolean).join(' · ');
  return <DialogHead title={title} meta={meta} onClose={onClose} sx={sx} />;
}

// The classic head ignores a close handler: the dialogs that opened it
// closed on the backdrop, as they still do.
ClassicDialogHead.propTypes = { onClose: PropTypes.func };

function ClassicDialogHead({ onClose, ...other }) {
  return <AllocationInfoHeader {...other} />;
}

// A footer the classic page has no piece for: the dialogs drew their own.
ClassicDialogFooter.propTypes = { children: PropTypes.node, hint: PropTypes.node, sx: PropTypes.object };

function ClassicDialogFooter({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 2.5,
        pb: 2,
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// THE CLASSIC COLUMN HEAD, as CandidateRow draws it (copied, not imported,
// so this file stays below the rows that read it).

const classicHeadSx = (theme, tone = 'soft') => {
  const light = theme.palette.mode === 'light';
  return {
    display: { xs: 'none', md: 'grid' },
    gap: 1.5,
    alignItems: 'center',
    px: 1.5,
    py: 1.125,
    ...(tone === 'solid'
      ? { bgcolor: INDIGO }
      : {
          bgcolor: alpha(INDIGO, light ? 0.1 : 0.22),
          borderBottom: `1px solid ${alpha(INDIGO, light ? 0.18 : 0.32)}`
        })
  };
};

const classicHeadCellSx = (theme, tone = 'soft') => ({
  fontFamily: TABLE_FONT,
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: '0.01em',
  lineHeight: 1.4,
  color: tone === 'solid' ? '#FFFFFF' : tableInkFor(theme)
});

// ---------------------------------------------------------------------------

const classicSkin = (theme) => ({
  name: 'classic',
  universe: false,
  font: TEAM_FONT,
  accent: accentInkFor(theme),
  fill: INDIGO,
  button: {
    primary: teamPrimaryButtonSx,
    secondary: teamSecondaryButtonSx(theme),
    soft: teamSoftButtonSx(theme),
    ghost: { textTransform: 'none' },
    link: { textTransform: 'none', color: 'link.default' },
    danger: {
      borderRadius: 1.25,
      px: 2.5,
      fontFamily: TEAM_FONT,
      fontWeight: 600,
      textTransform: 'none',
      color: '#FFFFFF',
      bgcolor: theme.palette.error.main,
      boxShadow: `0 8px 18px ${alpha(theme.palette.error.main, 0.32)}`,
      '&:hover': {
        bgcolor: theme.palette.error.dark,
        boxShadow: `0 10px 22px ${alpha(theme.palette.error.main, 0.4)}`
      },
      '&.Mui-disabled': {
        color: '#FFFFFF',
        bgcolor: theme.palette.error.main,
        opacity: 0.45,
        boxShadow: 'none'
      }
    },
    tone: (tone) => teamToneButtonSx(theme, tone),
    icon: () => ({ color: 'text.secondary' }),
    iconDanger: () => ({ color: 'error.main' })
  },
  Glyph: ClassicGlyph,
  Chip: ClassicChip,
  Panel: ClassicPanel,
  SectionCard: ClassicSectionCard,
  Eyebrow: ClassicEyebrow,
  Fact: ClassicFact,
  FactGroup: ClassicFactGroup,
  DialogHead: ClassicDialogHead,
  DialogFooter: ClassicDialogFooter,
  dialogSx: (sx) => sx,
  dialogProps: {},
  head: (tone = 'soft') => classicHeadSx(theme, tone),
  headCell: (tone = 'soft') => classicHeadCellSx(theme, tone),
  headEdge: (tone = 'soft') => (tone === 'solid' ? INDIGO : 'transparent'),
  box: {
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRadius: 1.5
  },
  sheet: {
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRadius: 1.5,
    overflow: 'hidden',
    bgcolor: 'background.paper'
  },
  rule: alpha(theme.palette.divider, 0.8),
  ruleSoft: alpha(theme.palette.divider, 0.7),
  rowToned: (tone) => {
    const colour = theme.palette[tone]?.main || theme.palette.success.main;
    return {
      bgcolor: alpha(colour, theme.palette.mode === 'light' ? 0.08 : 0.14),
      edge: colour
    };
  },
  pill: (active) => teamPillSx(theme, active),
  pillGroup: {},
  well: {
    px: 2,
    py: 1.5,
    borderRadius: 1.5,
    border: `1px solid ${theme.palette.divider}`
  },
  sunken: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.03 : 0.06),
  table: {},
  tableQuiet: {},
  tablePicker: {},
  tabs: {
    minHeight: 44,
    '& .MuiTab-root': {
      minHeight: 44,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 13.5,
      px: 1.5
    }
  },
  fields: {},
  pagination: tablePaginationSx(theme),
  footer: tableFooterSx(theme),
  menuPaper: {},
  alert: {},
  money: (value) => fCurrency(Number(value) || 0),
  percent: (value) => (value === null || value === undefined ? '—' : `${value}%`),
  count: (value) => fNumber(Number(value) || 0),
  // Hours are never rounded to a whole: 1,5 h is an hour and a half of
  // somebody's day and of somebody's invoice (added 08/10/2026 for the
  // mediation hours pass, where `count` would have printed 2).
  hours: (value) => (value === null || value === undefined || value === '' ? '' : String(value)),
  rate: (value, digits = 3) =>
    value === null || value === undefined || value === ''
      ? ''
      : `€${Number(value).toFixed(digits).replace(/0$/, '')}`
});

const universeSkin = (theme) => ({
  name: 'universe',
  universe: true,
  font: UNIVERSE_FONT,
  accent: accentOf(theme),
  fill: fillOf(theme),
  button: {
    primary: primaryButtonSx(theme),
    secondary: secondaryButtonSx(theme),
    soft: softButtonSx(theme),
    ghost: ghostButtonSx(theme),
    link: { ...ghostButtonSx(theme, 'small'), px: 0.75, minWidth: 0 },
    danger: destructiveButtonSx(theme),
    tone: (tone) => toneButtonSx(theme, tone),
    icon: (size = 32) => iconButtonSx(theme, size),
    iconDanger: (size = 32) => ({
      ...iconButtonSx(theme, size),
      color: toneInkOf(theme, 'error'),
      '&:hover': {
        color: toneInkOf(theme, 'error'),
        bgcolor: toneTintOf(theme, 'error', 'label')
      }
    })
  },
  Glyph: UniverseGlyph,
  Chip: UniverseChip,
  Panel: UniversePanel,
  SectionCard: UniverseSectionCard,
  Eyebrow: UniverseEyebrow,
  Fact: UniverseFact,
  FactGroup: UniverseFactGroup,
  DialogHead: UniverseDialogHead,
  DialogFooter,
  dialogSx: (sx) => ({ ...sx, ...dialogPaperSx(theme) }),
  dialogProps: {},
  head: (tone = 'soft') => (tone === 'solid' ? embeddedHeadSx(theme) : quietHeadSx(theme)),
  headCell: (tone = 'soft') => (tone === 'solid' ? embeddedHeadCellSx(theme) : quietHeadCellSx(theme)),
  headEdge: () => 'transparent',
  box: { borderTop: `1px solid ${hairlineOf(theme)}` },
  sheet: sheetSx(theme),
  rule: rowRuleOf(theme),
  ruleSoft: rowRuleOf(theme),
  rowToned: (tone) => ({
    bgcolor: toneTintOf(theme, tone, 'label'),
    edge: toneInkOf(theme, tone)
  }),
  pill: (active) => segmentSx(theme, active),
  pillGroup: segmentedTrackSx(theme),
  well: wellSx(theme),
  sunken: sunkenOf(theme),
  table: embeddedTableSx(theme),
  tableQuiet: secondaryTableSx(theme),
  tablePicker: pickerTableSx(theme),
  // the navy head band on a record's tables as well (the owner, 08/10/2026:
  // "in header we can use the primary color")
  tablePrimary: { ...secondaryTableSx(theme), ...primaryHeadSx() },
  tabs: textTabsSx(theme),
  fields: formControlsSx(theme),
  pagination: paginationSx(theme),
  footer: footerSx(theme),
  menuPaper: menuPaperSx(theme),
  alert: {
    borderRadius: `${RADIUS.small}px`,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    '& .MuiAlert-icon': { alignItems: 'center' },
    '& .MuiButton-root': { fontFamily: UNIVERSE_FONT, textTransform: 'none', fontWeight: 600 }
  },
  hover: accentTint(theme, 'hover'),
  money: (value) => fMoney(Number(value) || 0),
  percent: (value) => (value === null || value === undefined ? '—' : fPercent(value)),
  count: (value) => fCount(Number(value) || 0),
  hours: (value) => (value === null || value === undefined || value === '' ? '' : fHours(value)),
  rate: (value, digits = 3) => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    const text = n.toFixed(digits).replace(/0$/, '').replace('.', ',');
    return `€${text}`;
  }
});

/**
 * The recipes of the page's skin. Resolved once per theme and skin, so the
 * objects it returns are stable across renders.
 */
export function useSkin() {
  const name = useContext(SkinContext);
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  return useMemo(() => {
    const skin = name === 'universe' ? universeSkin(theme) : classicSkin(theme);
    if (name === 'universe') {
      // Dialogs go full-screen under `sm` (DESIGN.md §16).
      skin.dialogProps = phone ? { fullScreen: true } : {};
    }
    return skin;
  }, [name, theme, phone]);
}
